"use client";

import { useRef, useEffect, useState } from "react";
import Link from "next/link";
import Icon from "@/components/ui/AppIcon";


export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollY, setScrollY] = useState(0);
  const [particles, setParticles] = useState<Array<{ left: string; top: string; delay: string; duration: string }>>([]);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile on client-side
  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
    
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Generate particles on client-side only
  useEffect(() => {
    const particleCount = isMobile ? 4 : 8;
    setParticles(
      [...Array(particleCount)].map((_, i) => ({
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        delay: `${i * 2}s`,
        duration: `${15 + i * 2}s`,
      }))
    );
  }, [isMobile]);

  // Mouse parallax effect for 3D elements
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const rect = heroRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left - rect.width / 2) / rect.width;
      const y = (e.clientY - rect.top - rect.height / 2) / rect.height;
      setMousePosition({ x: x * 30, y: y * 30 });
    };

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 sm:pt-24 pb-12 sm:pb-16 bg-deep-dark"
    >
      {/* Layered Background with Radial Purple Glow */}
      <div className="absolute inset-0 bg-deep-dark" />
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse 100% 80% at 50% 40%, #151528 0%, #0F0F1A 60%)",
        }}
      />
      <div 
        className="absolute inset-0 pointer-events-none opacity-30 animate-ambient-shift will-change-transform"
        style={{
          background: "radial-gradient(ellipse 70% 60% at 50% 30%, rgba(91, 46, 255, 0.25) 0%, rgba(124, 77, 255, 0.1) 40%, transparent 70%)",
        }}
      />

      {/* Subtle Particle Movement (Hero Only) - Reduced on mobile */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {particles.map((particle, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-accent-primary rounded-full opacity-20 animate-particle-float will-change-transform"
            style={{
              left: particle.left,
              top: particle.top,
              animationDelay: particle.delay,
              animationDuration: particle.duration,
            }}
          />
        ))}
      </div>

      {/* Large Abstract 3D Orbital Ring with Cinematic Lighting - Hidden on mobile, smaller on tablet */}
      <div 
        className="absolute top-1/4 right-1/4 w-[300px] h-[300px] lg:w-[500px] lg:h-[500px] pointer-events-none hidden md:block"
        style={{
          transform: `translate(${mousePosition.x * 0.5}px, ${mousePosition.y * 0.5 - scrollY * 0.3}px)`,
          transition: "transform 0.3s ease-out"
        }}
      >
        <div className="relative w-full h-full animate-float-slow will-change-transform">
          {/* Outer orbital ring */}
          <div 
            className="absolute inset-0 rounded-full"
            style={{
              background: "conic-gradient(from 0deg, rgba(91, 46, 255, 0.3), rgba(124, 77, 255, 0.2), rgba(159, 122, 234, 0.3), rgba(91, 46, 255, 0.3))",
              boxShadow: "0 0 80px rgba(91, 46, 255, 0.4), inset 0 0 60px rgba(91, 46, 255, 0.2)",
              backdropFilter: "blur(40px)",
              border: "2px solid rgba(91, 46, 255, 0.3)",
              animation: "orbit 25s linear infinite",
            }}
          />
          {/* Inner orb with chrome effect */}
          <div 
            className="absolute inset-[50px] md:inset-[80px] rounded-full"
            style={{
              background: "radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0.05) 40%, rgba(91, 46, 255, 0.15) 100%)",
              boxShadow: "0 20px 80px rgba(91, 46, 255, 0.5), inset 0 0 60px rgba(255, 255, 255, 0.1), inset 20px 20px 40px rgba(255, 255, 255, 0.15)",
              backdropFilter: "blur(40px)",
              border: "1px solid rgba(255, 255, 255, 0.2)"
            }}
          />
          {/* Cinematic light reflection */}
          <div 
            className="absolute top-[60px] left-[60px] md:top-[100px] md:left-[100px] w-20 h-20 md:w-32 md:h-32 rounded-full"
            style={{
              background: "radial-gradient(circle, rgba(255, 255, 255, 0.7) 0%, transparent 70%)",
              filter: "blur(25px)"
            }}
          />
          {/* Soft purple glow */}
          <div 
            className="absolute inset-0 rounded-full animate-glow-pulse will-change-transform"
            style={{
              background: "radial-gradient(circle, rgba(91, 46, 255, 0.4) 0%, transparent 70%)",
              filter: "blur(60px)"
            }}
          />
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center">
        {/* Label */}
        <div className="mb-4 sm:mb-6 flex justify-center animate-fade-in-up">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full bg-glass-white border border-glass-border text-xs sm:text-sm font-medium text-text-secondary backdrop-blur-xl">
            <span className="w-2 h-2 rounded-full bg-accent-primary animate-glow-pulse" />
            <span className="hidden sm:inline">European-Operated · Shenzhen-Based · Tech-Driven</span>
            <span className="sm:hidden">Tech-Driven Logistics</span>
          </span>
        </div>

        {/* Headline with refined typography */}
        <h1
          className="font-heading font-extrabold text-text-primary mb-4 sm:mb-6 animate-fade-in-up px-2"
          style={{
            fontSize: "clamp(36px, 8vw, 82px)",
            lineHeight: "1.1",
            letterSpacing: "-0.025em",
            animationDelay: "0.1s"
          }}
        >
          Shenzhen-based fulfilment.
          <br />
          <span className="gradient-text">European standards.</span>
        </h1>

        {/* Subheadline */}
        <p
          className="text-text-secondary mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed animate-fade-in-up px-4"
          style={{
            fontSize: "clamp(16px, 2.5vw, 20px)",
            animationDelay: "0.2s"
          }}
        >
          Factory-direct sourcing, secure warehousing, and 5–8 day worldwide shipping — all managed by a European-operated team in the heart of China's manufacturing capital.
        </p>

        {/* CTA Buttons - Stacked on mobile */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5 animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
          <Link
            href="/contact"
            className="group relative inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 rounded-2xl text-white font-bold overflow-hidden transition-all duration-500 hover:scale-105 w-full sm:w-auto min-h-[52px] sm:min-h-[56px]"
            style={{
              fontSize: "clamp(14px, 2vw, 16px)",
              background: "linear-gradient(135deg, #3B1A99 0%, #4520CC 50%, #5530E6 100%)",
              boxShadow: "0 0 20px rgba(69, 32, 204, 0.25), 0 8px 24px rgba(0, 0, 0, 0.3)",
            }}
          >
            <Icon name="PhoneIcon" size={18} className="relative z-10" />
            <span className="relative z-10">Book a Free Call</span>
            {/* Micro shimmer animation */}
            <div 
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{
                background: "linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.2) 50%, transparent 100%)",
                backgroundSize: "200% 100%",
                animation: "shimmer 3s linear infinite",
              }}
            />
          </Link>
          <Link
            href="/services"
            className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 rounded-2xl border-2 border-glass-border bg-glass-white backdrop-blur-xl text-text-primary font-bold transition-all duration-300 hover:bg-opacity-10 hover:border-accent-primary w-full sm:w-auto min-h-[52px] sm:min-h-[56px]"
            style={{
              fontSize: "clamp(14px, 2vw, 16px)",
            }}
          >
            Explore Services
            <Icon name="ArrowRightIcon" size={18} />
          </Link>
        </div>

        {/* Trust indicators - Responsive layout */}
        <div className="mt-12 sm:mt-16 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12 text-sm text-text-secondary animate-fade-in-up" style={{ animationDelay: "0.4s" }}>
          <div className="flex items-center gap-2">
            <Icon name="CheckCircleIcon" size={18} className="text-accent-primary" />
            <span className="text-xs sm:text-sm">5–8 day delivery to US & EU</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="CheckCircleIcon" size={18} className="text-accent-primary" />
            <span className="text-xs sm:text-sm">Under 0.3% error rate</span>
          </div>
          <div className="flex items-center gap-2">
            <Icon name="CheckCircleIcon" size={18} className="text-accent-primary" />
            <span className="text-xs sm:text-sm">48-hour onboarding</span>
          </div>
        </div>
      </div>
    </section>
  );
}